import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "./Reveal";
import { scrollToTarget } from "@/lib/lenis";
import { GOOGLE_FORM_URL } from "@/lib/links";
import { useSiteImages } from "@/lib/useSiteImages";

const HERO_IMG =
  "https://images.unsplash.com/photo-1461468611824-46457c0e11fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxzcGlyaXR1YWwlMjB3b21hbiUyMG1lZGl0YXRpbmclMjBuYXR1cmV8ZW58MHx8fHwxNzg1ODQxMjI3fDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const siteImages = useSiteImages();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setIsDesktop(mq.matches);
    const fn = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center px-6 md:px-12 pt-32 pb-16 overflow-hidden"
    >
      <motion.div style={{ y: textY }} className="relative z-10 max-w-5xl">
        <MaskedLine delay={0.25}>
          <span
            data-testid="hero-overline"
            className="text-xs md:text-sm uppercase tracking-[0.3em] text-amethyst font-medium"
          >
            A 3-month transformational journey
          </span>
        </MaskedLine>
        <h1 className="font-serif text-soil text-[17vw] md:text-[11vw] leading-[0.9] tracking-tight mt-6">
          <MaskedLine delay={0.4}>Embodied</MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="italic text-amethyst">Awakening</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.85} className="mt-8 max-w-xl">
          <p
            data-testid="hero-subtitle"
            className="text-base md:text-lg text-clay leading-relaxed"
          >
            A grounded journey through spiritual awakening, moving from
            overwhelm into clarity, stability, and purposeful direction.
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-begin-button"
            onClick={() => scrollToTarget("#enquire")}
            className="rounded-full bg-gold text-soil text-xs uppercase tracking-[0.18em] px-8 py-4 hover:bg-amethyst hover:text-cream transition-colors duration-300"
          >
            Begin your journey
          </button>
          <a
            data-testid="hero-discovery-button"
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full border border-soil/30 text-soil text-xs uppercase tracking-[0.18em] px-8 py-4 hover:border-amethyst hover:text-amethyst transition-colors duration-300"
          >
            Book a free discovery call
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-0 mt-14 w-full max-w-[320px] md:max-w-[440px] md:w-[30vw] md:absolute md:right-[4%] md:top-[12%] md:mt-0"
        style={{ y: isDesktop ? imgY : 0 }}
      >
        <div className="arch-mask overflow-hidden aspect-[3/4.2]">
          <img
            src={siteImages.hero || HERO_IMG}
            alt="Woman meditating in stillness by the sea"
            data-testid="hero-image"
            className="w-full h-full object-cover"
          />
        </div>
        <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-clay/70 text-right">
          Returning home to the body
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        className="absolute bottom-8 left-6 md:left-12 hidden md:flex items-center gap-3 text-clay/70"
      >
        <span className="block w-10 h-px bg-clay/40" />
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll softly</span>
      </motion.div>
    </section>
  );
};
